import { FC, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ItemType } from "antd/lib/menu/interface";
import {
  TeamOutlined,
  HomeOutlined,
  ContactsOutlined,
  ReadOutlined,
  FileDoneOutlined,
  AudioMutedOutlined,
  ExpandOutlined,
  UserSwitchOutlined,
  ScanOutlined,
} from "@ant-design/icons";

import { useClientSettings } from "@hooks/useClientSettings";
import { useCorban } from "@hooks/useCorban";
import usePermissions from "@hooks/usePermissions";
import { featureFlags } from "@settings";

import { CorbanSelector } from "./CorbanSelector";
import { Menu, Container, Content, CollapsedCorbanIcon } from "./styles";
import { LOCAL_STORAGE } from "@constants";
import { theme } from "antd";
import { isTrainingTabEnabled } from "../../settings/whiteLabel";

interface Props {
  onItemClick?: () => void;
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

export const SidebarMenuV2: FC<Props> = ({
  onItemClick,
  collapsed,
  onCollapse,
}) => {
  const BENEFIT_CARD_NAME = useClientSettings().benefitCardName;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { routes, homeRoute } = usePermissions();
  const { corbans } = useCorban();
  const filteredOptions =
    corbans.filter((option) => option.url?.includes(location.origin)).length >
    1;

  const currentRoute = pathname.substring(1);
  const routeSplit = currentRoute.split("/").filter(Boolean);
  const currentSelectedSub1 = routeSplit[0];
  const currentSelectedSub2 = routeSplit.slice(0, 2).join("/");
  const hasJourney = localStorage.getItem(LOCAL_STORAGE.LAST_JOURNEY);

  const hasUsersCrud = featureFlags.enableUsersCrud;
  const isRetentionLayout = featureFlags.enableContractsRetentionLayout;
  const initialOpenKeys = [currentSelectedSub1, currentSelectedSub2].filter(
    (key): key is string => !!key,
  );

  const [openKeys, setOpenKeys] = useState<string[]>(initialOpenKeys);

  const isAllowedItemType = useCallback(
    (itemType: ItemType) =>
      itemType?.key ? !!routes[`/${itemType.key}`] : true,
    [routes],
  );

  const { token } = theme.useToken();

  const getAllowedItemTypes = useCallback(
    (
      itemTypeList: ((ItemType & { children?: ItemType[] }) | null)[],
    ): ItemType[] => {
      return itemTypeList.reduce((allowedItemTypes: ItemType[], itemType) => {
        if (itemType && isAllowedItemType(itemType)) {
          if (itemType.children) {
            return [
              ...allowedItemTypes,
              {
                ...itemType,
                children: getAllowedItemTypes(itemType.children),
              },
            ];
          }

          return [...allowedItemTypes, itemType];
        }

        return allowedItemTypes;
      }, []);
    },
    [isAllowedItemType],
  );
  const menuItems = useMemo(() => {
    const fullMenu: ItemType[] = [
      {
        label: "Serviços",
        key: "servicos",
        icon: <HomeOutlined style={{ paddingLeft: "4.5px" }} />,
        children: [
          {
            label: "INSS",
            key: "inss",
            icon: "",
            children: [
              {
                label: "Multiproduto",
                key: "inss/multiproduto",
                icon: "",
              },
              ...(featureFlags.enableMarginIncrease
                ? [
                    {
                      label: "Aumento Salarial",
                      key: "inss/aumento-margem",
                      icon: "",
                    },
                  ]
                : []),
              ...(featureFlags.enableBatchTypingMarginIncrease
                ? [
                    {
                      label: "Digitação em lote (Aumento de Margem)",
                      key: "inss/aumento-margem-lote",
                      icon: "",
                    },
                  ]
                : []),
              {
                label: "Margem Livre",
                key: "inss/margem-livre",
                icon: "",
              },
              {
                label: "Portabilidade",
                key: "inss/portabilidade",
                icon: "",
              },
              {
                label: "Portabilidade + Refin",
                key: "inss/portabilidade-refin",
                icon: "",
              },
              {
                label: "Refinanciamento",
                key: "inss/refinanciamento",
                icon: "",
              },
            ],
          },
          {
            label: "Cartão Consignado",
            key: "cartao",
            icon: "",
            children: [
              {
                label: BENEFIT_CARD_NAME,
                key: "cartao/cartao-beneficio",
                icon: "",
              },
              {
                label: "Agregação de Margem",
                key: "cartao/agregacao-margem",
                icon: "",
              },
            ],
          },
          (featureFlags.enablePublicConsig && {
            label: "Consignado Público",
            key: "consignado-publico",
            icon: "",
          }) as ItemType,
          (featureFlags.enableProductReceba && {
            label: "Receba",
            key: "receba",
            icon: "",
          }) as ItemType,
          ((featureFlags.enablePrivateConsigLoan ||
            featureFlags.enablePrivateConsigCef) && {
            label: "Privado (CLT)",
            key: "consignado-privado",
            icon: "",
          }) as ItemType,
        ],
      },
      ...(featureFlags.enablePortabilityAttack
        ? [
            {
              label: isRetentionLayout ? "Retenção" : "Portabilidade Ataque",
              key: "portabilidade-ataque",
              icon: isRetentionLayout ? (
                <ExpandOutlined style={{ paddingLeft: "4.5px" }} />
              ) : (
                <ScanOutlined />
              ),
              ...(isRetentionLayout && {
                children: [
                  {
                    label: "Contratos",
                    key: "portabilidade-ataque/contratos",
                    icon: "",
                  },
                  {
                    label: "Acompanhamento",
                    key: "portabilidade-ataque/acompanhamento",
                    icon: "",
                  },
                ],
              }),
            },
          ]
        : []),
      {
        label: "Contratos",
        key: "contratos",
        icon: <ContactsOutlined />,
      },
    ];

    if (featureFlags.enableSupportMaterials) {
      fullMenu.push({
        label: "Materiais de Apoio",
        key: "materiais-de-apoio",
        icon: <FileDoneOutlined />,
      });
    }

    if (
      featureFlags.enableDoNotDisturbTab ||
      featureFlags.enableDoNotDisturbTabCef
    ) {
      fullMenu.push({
        label: "Não Me Perturbe",
        key: "nao-me-perturbe",
        icon: <AudioMutedOutlined />,
      });
    }

    if (isTrainingTabEnabled()) {
      fullMenu.push({
        label: "Treinamentos",
        key: "treinamentos",
        icon: <ReadOutlined />,
      });
    }

    if (hasUsersCrud) {
      fullMenu.push({
        label: "Usuários",
        key: "usuarios",
        icon: <TeamOutlined />,
      });
    }

    return getAllowedItemTypes(fullMenu);
  }, [getAllowedItemTypes, hasUsersCrud, BENEFIT_CARD_NAME, isRetentionLayout]);

  const handleClick = ({ key }: { key: string }) => {
    const isJourneyEnd = pathname.includes(key) && !hasJourney;
    onItemClick?.();
    if (isJourneyEnd) return void navigate(homeRoute);
    void navigate(`/${key}`);
  };

  return (
    <Container>
      <Content>
        {collapsed && filteredOptions ? (
          <CollapsedCorbanIcon onClick={() => onCollapse?.(!collapsed)}>
            <UserSwitchOutlined />
          </CollapsedCorbanIcon>
        ) : (
          <CorbanSelector />
        )}
        <Menu
          mode="inline"
          items={menuItems}
          onClick={handleClick}
          selectedKeys={[currentRoute]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          $primaryColor={token.colorPrimary}
        />
      </Content>
    </Container>
  );
};
